import { useMemo } from 'react';
import { UserPermissions } from './useUserPermissions';
import { fdCan, matrixRoleOf, roleCan, type FdSection } from '../utils/frontDeskMatrix';
import { buildTenantNav, usesTenantNav } from './tenantNav';

export interface MenuItemData {
  title: string;
  itemId: string;
  iconName: string;
  subItems?: MenuItemData[];
}

export const useMenuBuilder = (
  permissions: UserPermissions
): MenuItemData[] => {
  return useMemo(() => {
    // The tenant Front Desk sees every section the matrix (specs/front-desk-tenant-permisos.md) lets it view,
    // in addition to whatever its own groups grant; its actions are gated on each page.
    const fdSees = (section: FdSection) => permissions.isTenantFrontDesk && fdCan(section, 'view');
    // Event-dashboard-only role: a single entry to the business's events list,
    // from which they open read-only dashboards. Nothing else is exposed.
    // isEventDashboardOnly is mutually exclusive with isAdmin/isEventsManager
    // (see useUserPermissions), so this never runs alongside the top-level
    // "Events" category below — no duplicate entry is produced.
    if (permissions.isEventDashboardOnly) {
      return [
        {
          title: "Events",
          itemId: "#eventsad/list",
          iconName: "Calendar",
        },
      ];
    }

    // The tenant admin, the tenant Front Desk and the estate group roles get the menu of ADMIN-NAV.md
    // (tenantNav.ts), each matrix role the sections its own matrix lets it view; every other role keeps the one below.
    if (usesTenantNav(permissions)) {
      const role = matrixRoleOf(permissions);
      return buildTenantNav(permissions, (section) => (role ? roleCan(role, section, 'view') : false));
    }

    const menuItems: MenuItemData[] = [];

    // Dashboard - hidden for invitation-only users, property managers (who go to /property-admin)
    // and business admins (S-16: the dashboard is tenant-wide)
    if (
      (!permissions.isInvitationOnly || fdSees('dashboard')) &&
      !(permissions.isPropertyManager && !permissions.isAdmin) &&
      !(permissions.isBusinessAdmin && !permissions.isAdmin)
    ) {
      menuItems.push({
        title: "Dashboard",
        itemId: "#dashboard",
        iconName: "LayoutDashboard",
      });
    }

    // Passes (the Invitations screen) - admin, business admin (their own business's passes, Guest one day),
    // invitation manager (business/residential/all), or property manager
    if (
      permissions.isAdmin ||
      permissions.isBusinessAdmin ||
      permissions.isInvitationManager ||
      permissions.isResidentialInvitationManager ||
      permissions.isAllInvitationManager ||
      permissions.isPropertyManager
    ) {
      menuItems.push({
        title: "Passes",
        itemId: "#invitations",
        iconName: "UserPlus",
      });
    }

    // Arrivals is gone for every role (Rodrigo, 29 Oct): Activity replaces it, and /arrivals redirects there.

    // Business - mostrar si tiene al menos uno de los permisos de business
    // (Events ya no cuenta aca: es categoria de primer nivel, ver mas abajo)
    // The Property Manager has no Business (Rodrigo, 29 Oct): the bookings grant it gets as front desk does not open it.
    const hasBusinessAccess =
      permissions.isAdmin ||
      permissions.isPaymentsManager ||
      permissions.isServicesManager ||
      (permissions.isBookingsManager && !permissions.isPropertyManager) ||
      fdSees('business');

    if (hasBusinessAccess) {
      const businessSubItems: MenuItemData[] = [];

      // List (all businesses for admin, own business for others)
      // The tenant Front Desk has no business of its own (no «My business»), but sees the list of the tenant's
      // businesses and their files, read-only.
      if (!permissions.isTenantFrontDesk || fdSees('business')) {
        businessSubItems.push({
          // SAM-198: a business's own admin goes straight to their business page.
          title: permissions.isAdmin || permissions.isTenantFrontDesk ? "List" : "My business",
          itemId: "#business/list",
          iconName: "List",
        });
      }

      // Payments
      if (permissions.isAdmin || permissions.isPaymentsManager) {
        businessSubItems.push({
          title: "Payments",
          itemId: "#payments",
          iconName: "CreditCard",
        });
      }

      // Products
      if (permissions.isAdmin || permissions.isPaymentsManager || fdSees('business')) {
        businessSubItems.push({
          title: "Products",
          itemId: "#products",
          iconName: "ShoppingBag",
        });
      }

      // Services
      if (permissions.isAdmin || permissions.isServicesManager || fdSees('business')) {
        businessSubItems.push({
          title: "Services",
          itemId: "#services",
          iconName: "Briefcase",
        });
      }

      // Activity (the /bookings route): every business's bookings and passes for admins, the business's own bookings otherwise.
      if (permissions.isAdmin || permissions.isBookingsManager) {
        businessSubItems.push({
          title: "Activity",
          itemId: "#bookings",
          iconName: "CalendarDays",
        });
      }

      menuItems.push({
        title: "Business",
        itemId: "#business",
        iconName: "Building2",
        subItems: businessSubItems,
      });
    }

    // Events - categoria de primer nivel (promovida desde Business, SAM-380)
    if (permissions.isAdmin || permissions.isEventsManager || fdSees('events')) {
      menuItems.push({
        title: "Events",
        itemId: "#eventsad",
        iconName: "Calendar",
        subItems: [
          {
            title: "List",
            itemId: "#eventsad/list",
            iconName: "List",
          },
          {
            title: "Access Settings",
            itemId: "#eventsad/access-settings",
            iconName: "Settings",
          },
        ],
      });
    }

    // Users - admin o users manager
    if (permissions.isAdmin || permissions.isUsersManager || fdSees('users')) {
      menuItems.push({
        title: "Users",
        itemId: "#users",
        iconName: "User",
      });
    }

    // Estates - admin o estates manager
    if (permissions.isAdmin || permissions.isEstatesManager || fdSees('estates')) {
      menuItems.push({
        title: "Estates",
        itemId: "#estates",
        iconName: "Home",
      });
    }

    // Property Admin - admin o property manager
    if (permissions.isAdmin || permissions.isPropertyManager) {
      menuItems.push({
        title: "Property Admin",
        itemId: "#property-admin",
        iconName: "Building2",
      });
    }

    // App Content - manages what users see in the mobile app
    const hasAppAccess = permissions.isAdmin || permissions.isCMSManager || permissions.isRolesManager
      || fdSees('cms') || fdSees('notifications') || fdSees('employees');

    if (hasAppAccess) {
      const appSubItems: MenuItemData[] = [];

      // CMS - groups Home Screen, Articles, Ads
      if (permissions.isAdmin || permissions.isCMSManager || fdSees('cms')) {
        appSubItems.push({
          title: "CMS",
          itemId: "#cms",
          iconName: "LayoutGrid",
          subItems: [
            {
              title: "Home Screen",
              itemId: "#settings/home-screen",
              iconName: "Smartphone",
            },
            {
              title: "Articles",
              itemId: "#settings/articles",
              iconName: "FileText",
            },
            {
              title: "Ads",
              itemId: "#settings/ads",
              iconName: "Image",
            },
          ],
        });
      }

      // Roles
      if (permissions.isAdmin || permissions.isRolesManager) {
        appSubItems.push({
          title: "Roles",
          itemId: "#settings/roles",
          iconName: "ShieldCheck",
        });
      }

      // Invitation Types
      if (permissions.isAdmin || permissions.isRolesManager) {
        appSubItems.push({
          title: "Invitation Types",
          itemId: "#settings/invitation-types",
          iconName: "Tag",
        });
      }

      // Gatekeeper Users
      if (permissions.isAdmin || fdSees('employees')) {
        appSubItems.push({
          title: "Gatekeeper Users",
          itemId: "#settings/gatekeeper-users",
          iconName: "KeyRound",
        });
      }

      // Gates (SAM-946): the tenant's external and internal gates, tenant admin and Xhinola only.
      if (permissions.isAdmin) {
        appSubItems.push({
          title: "Gates",
          itemId: "#settings/gates",
          iconName: "Landmark",
        });
      }

      // Push Notifications
      if (permissions.isAdmin || fdSees('notifications')) {
        appSubItems.push({
          title: "Push Notifications",
          itemId: "#settings/push-notifications",
          iconName: "Bell",
        });
        appSubItems.push({
          title: "Notification Templates",
          itemId: "#settings/notification-templates",
          iconName: "FileText",
        });
      }

      menuItems.push({
        title: "App",
        itemId: "#app",
        iconName: "AppWindow",
        subItems: appSubItems,
      });
    }

    // Settings - system/admin settings
    // SAM-535: an Xhinola operator may hold none of the tenant admin groups, so Settings has to
    // open for them too — otherwise the only entry they have would be unreachable.
    const hasSettingsAccess =
      permissions.isAdmin || permissions.isTenantSettings || permissions.isXhinolaAdmin;

    if (hasSettingsAccess) {
      const settingsSubItems: MenuItemData[] = [];

      // Tenant Settings
      if (permissions.isAdmin || permissions.isTenantSettings) {
        settingsSubItems.push({
          title: "Tenant Settings",
          itemId: "#settings/tenant",
          iconName: "Building",
        });
      }

      // SAM-535 — merchant configuration. Shown for the `xhinola_admin` group; the backend also
      // requires an operator pool, which the browser cannot see, so this is a UX decision and the
      // 403 is the actual boundary.
      if (permissions.isXhinolaAdmin) {
        settingsSubItems.push({
          title: "Merchant Configuration",
          itemId: "#settings/merchant",
          iconName: "CreditCard",
        });
      }

      // Permission Groups
      if (permissions.isAdmin) {
        settingsSubItems.push({
          title: "Permissions",
          itemId: "#settings/permissions",
          iconName: "ShieldCheck",
        });
      }


      menuItems.push({
        title: "Settings",
        itemId: "#settings",
        iconName: "Settings",
        subItems: settingsSubItems,
      });
    }

    return menuItems;
  }, [
    permissions.isAdmin,
    permissions.isBusinessAdmin,
    permissions.isPropertyManager,
    permissions.isInvitationManager,
    permissions.isResidentialInvitationManager,
    permissions.isAllInvitationManager,
    permissions.isInvitationOnly,
    permissions.isDashboardOnly,
    permissions.isEventDashboardOnly,
    permissions.isTenantSettings,
    permissions.isPaymentsManager,
    permissions.isUsersManager,
    permissions.isEstatesManager,
    permissions.isEventsManager,
    permissions.isInsightsManager,
    permissions.isCMSManager,
    permissions.isRolesManager,
    permissions.isServicesManager,
    permissions.isBookingsManager,
    permissions.isTenantFrontDesk,
    permissions.isEstateGroupAdmin,
    permissions.isEstateGroupFrontDesk,
    permissions.isXhinolaAdmin,
  ]);
};
